export const offeringPropertyQueryKeys = {
  all: ['property', 'offering'] as const,
  // An offering's seller settlement (Settlement tab). Every action answers with
  // the refreshed settlement, written straight back under this key.
  settlement: (offeringId: string) =>
    [...offeringPropertyQueryKeys.all, 'settlement', offeringId] as const,
  // Settlement status per offering on one list page (Fully Funded tag).
  settlementStatuses: (offeringIds: string[]) =>
    [...offeringPropertyQueryKeys.all, 'settlement-statuses', offeringIds] as const,
  availableProperties: () => [...offeringPropertyQueryKeys.all, 'available-properties'] as const,
  details: (id: string) => [...offeringPropertyQueryKeys.all, 'details', id] as const,
  // Single offering by its own id (read-only Offering Details page). Distinct
  // from `details`, which keys a property by id.
  offeringDetail: (id: string) =>
    [...offeringPropertyQueryKeys.all, 'offering-detail', id] as const,
  // The offering instrument's documents (whitepaper). Keyed by offering because
  // that is what every caller holds; the instrument is resolved server-side.
  whitepaper: (offeringId: string) =>
    [...offeringPropertyQueryKeys.all, 'whitepaper', offeringId] as const,
  financials: (id: string) => [...offeringPropertyQueryKeys.all, 'financials', id] as const,
  offerings: (propertyId: string) =>
    [...offeringPropertyQueryKeys.all, 'offerings', propertyId] as const,
  // `filters` is typed `Record<string, unknown>` (not `GetAllOfferingsParams`)
  // because TanStack's `MaybeRefDeep` queryKey type can't recurse an interface
  // without an index signature; the caller narrows with a single boundary cast.
  allOfferings: (filters: Record<string, unknown>) =>
    [...offeringPropertyQueryKeys.all, 'all-offerings', filters] as const,
  statusCounts: () => [...offeringPropertyQueryKeys.all, 'status-counts'] as const,
  // The whole offerings directory swept into one entry, for cross-module
  // "which offering is behind this property" lookups. Distinct from
  // `allOfferings`, which is one filtered page of the operator's own view.
  offeringsLookup: () => [...offeringPropertyQueryKeys.all, 'offerings-lookup'] as const,
  // The fee terms an offering charges investors. Keyed by offering, because the
  // set is the offering's own copy — two offerings on one property can charge
  // different fees once either has been edited.
  fees: (offeringId: string) => [...offeringPropertyQueryKeys.all, 'fees', offeringId] as const,
  // The same set for an offering that does not exist yet, which is why it is
  // keyed by property AND type: the type decides which lifecycle stages apply,
  // so switching it on the form has to re-read rather than serve the other set.
  feePreview: (propertyId: string, offeringType: string) =>
    [...offeringPropertyQueryKeys.all, 'fee-preview', propertyId, offeringType] as const,
  feeVersions: (offeringId: string, feeDefinitionId: string) =>
    [...offeringPropertyQueryKeys.all, 'fee-versions', offeringId, feeDefinitionId] as const,
  // The Ctrl-Alt listing held against one offering (VAISP onboarding drawer).
  vaspListing: (offeringId: string) =>
    [...offeringPropertyQueryKeys.all, 'vasp-listing', offeringId] as const,
  // A property's primary offering folded together with that offering's Ctrl-Alt
  // listing — the token a trustline is opened against. Keyed by property because
  // that is the only identity its consumer (wallet management) holds.
  tokenIdentity: (propertyId: string) =>
    [...offeringPropertyQueryKeys.all, 'token-identity', propertyId] as const,
  // Every money movement on one offering and its property (details Overview).
  transactions: (offeringId: string) =>
    [...offeringPropertyQueryKeys.all, 'transactions', offeringId] as const,
  // The investors one offering issued tokens to (details Investors & Orders).
  holders: (offeringId: string) =>
    [...offeringPropertyQueryKeys.all, 'holders', offeringId] as const,
}
